import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  BookOpen,
  CalendarDays,
  Clock,
  Eye,
  FileText,
  Heart,
  House,
  Info,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Share2,
  Sparkles,
  Trash2,
  User,
  Users,
  X,
} from "lucide-react";
import {
  createSchedule,
  deleteSchedule,
  getLessonPlanCatalog,
  getLessonPlanChildrenProgress,
  getPlanRefusals,
  getScheduleShareKey,
  getSchedules,
  updateSchedule,
  updateScheduleStatus,
  uploadScheduleAttachments,
} from "../api";
import showToast from "../utils/toast";
import MentionTextarea from "../components/common/MentionTextarea";
import { COMPUTER_APPS, appMeta } from "../constants/lessonPlanCatalog";
import { STATUS_LABEL, openScheduleAttachment, planRowsFromText, visitLinkText, visitShareUrl } from "./DailyScheduleVisitModals";
import LessonPlanPicker from "./LessonPlanPicker";
import {
  LEARNING_COLUMNS,
  buildColumnOptions,
  emptyLearning,
  learningFromSaved,
  proposeRow,
  savedLearning,
  trackerComputerGroups,
  usedColumns,
} from "./lessonPlanColumns";
import {
  COLUMN_ICON_OF,
  COMPUTER_GROUPS,
  FixedLearning,
  FixedValue,
  PlanTableHead,
  STATIC_OPTIONS,
  fixedAppMeta,
  rowLearning,
} from "./lessonPlanCells";
import {
  CheckPicker,
  NativePickerField,
  addDays,
  ampm,
  centreIdOf,
  coachIdOf,
  confirmDialog,
  dateKey,
  escapeHtml,
  flattenSchedules,
  fromKey,
  hhmm,
  idOf,
  isStudent,
  resultDialog,
  scheduleDate,
} from "./dailyScheduleShared";

const DRAFT_KEY = "ds-lesson-plan-draft";
// Mirrors the backend upload middleware (middleware/upload.js).
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ATTACHMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv";
const fileSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const longDate = (key) => {
  const d = fromKey(key);
  return `${d.toLocaleDateString("en-GB", { weekday: "short" })}, ${d.getDate()} ${d.toLocaleDateString("en-GB", { month: "short" })} ${d.getFullYear()}`;
};
const shortDate = (d) => `${d.getDate()} ${d.toLocaleDateString("en-GB", { month: "short" })} ${d.getFullYear()}`;

const readDraft = () => {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { return null; }
};

// "Asmita", "Asmita and Deepak", "Asmita, Deepak and Rizwana".
const joinNames = (names) => (names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] || "");

const loadProgress = (centreId) => Promise.resolve()
  .then(() => getLessonPlanChildrenProgress(centreId))
  .then((res) => {
    const data = res?.success === false ? {} : res?.data || {};
    return {
      centreId,
      ready: true,
      failed: res?.success === false,
      byId: Object.fromEntries((data.children || []).map((c) => [String(c._id), c])),
      maxScore: data.maxScore || 30,
      columnNames: data.columnNames || {},
    };
  })
  .catch(() => ({ centreId, ready: true, failed: true, byId: {}, maxScore: 30, columnNames: {} }));

// View mode ("/daily-schedule/view"): the same boxes as the controls, without anything to change.
function Fixed({ icon, children }) {
  return <div className="ds-native lp-control lp-fixed">{icon}<span>{children}</span></div>;
}

// "Previous Two Visits" › View: one earlier visit (every coach's copy of that day) in the plan table.
const MEMBER_STATUS = { pending: "Scheduled", inprogress: "In progress", completed: "Completed", cancelled: "Cancelled" };
const savedRows = (s) => (s.lessonPlan?.children?.length ? s.lessonPlan.children : planRowsFromText(s.description));
// Coaches whose copies are the same are shown together, under one table.
const visitPlanGroups = (list) => {
  const groups = new Map();
  list.forEach((s) => {
    const rows = savedRows(s);
    const key = JSON.stringify([rows, s.learningsFocus || "", s.specialInstructions || "", s.healthCheck || "", rows.length ? "" : s.description || ""]);
    if (groups.has(key)) groups.get(key).members.push(s);
    else groups.set(key, { key, rows, first: s, members: [s] });
  });
  return [...groups.values()];
};

// What coaches recorded in the Syllabus Tracker when a child refused an activity the visit's plan
// gave them (GET .../syllabus-tracker/plan-refusals): (childId, column, value) -> refusal or null.
// Nothing while it loads or when it can't be loaded, so the plan shows as before.
const usePlanRefusals = (balagruhaId, date) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!balagruhaId || !date) { setList([]); return undefined; }
    let cancelled = false;
    Promise.resolve()
      .then(() => getPlanRefusals({ balagruhaId, date }))
      .then((res) => { if (!cancelled) setList(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setList([]); });
    return () => { cancelled = true; };
  }, [balagruhaId, date]);
  // Only for the activity as it is still planned: a refusal of an item the admin has since changed is
  // left out. Its column first; a one-item row's column is worked out, so the item alone also counts.
  return useCallback((childId, column, value) => {
    if (!childId || !value) return null;
    const mine = list.filter((r) => r.studentId === String(childId) && r.assignedValue === value);
    return mine.find((r) => r.column === column) || mine[0] || null;
  }, [list]);
};

// Under a planned cell: the child refused it, and what they did instead. `auto`: the coach didn't
// record it, the Syllabus Tracker scores show another activity done instead ("Not followed").
function PlanRefusalNote({ refusal }) {
  if (!refusal) return null;
  const word = refusal.auto ? "Not followed" : "Refused";
  const text = refusal.doneName ? `${word} · did ${refusal.doneName}` : `${word} · did nothing else`;
  const source = refusal.auto ? "From the Syllabus Tracker scores" : refusal.recordedBy && `Recorded by ${refusal.recordedBy}`;
  const full = [text, refusal.reason, source].filter(Boolean).join(" — ");
  return (
    <p className={`lp-refusal${refusal.auto ? " is-auto" : ""}`} title={full} aria-label={full}>
      <Ban size={13} aria-hidden="true" />
      <span>{text}{refusal.reason ? <small>{refusal.reason}</small> : null}</span>
    </p>
  );
}

function PreviousVisitDialog({ visit, centreName, coachName, onClose }) {
  const groups = visitPlanGroups(visit.list);
  const refusalOf = usePlanRefusals(centreIdOf(visit.first), dateKey(scheduleDate(visit.first)));
  return (
    <div className="ds-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ds-modal__card lp-visit" role="dialog" aria-modal="true" aria-labelledby="lp-visit-title">
        <div className="ds-modal__head">
          <h2 id="lp-visit-title">Visit on {shortDate(scheduleDate(visit.first))}</h2>
          <button type="button" aria-label="Close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="lp-visit__body">
          <div className="lp-visit__facts">
            <div className="ds-field"><span>Coaches</span><Fixed icon={<Users size={17} fill="currentColor" />}>{visit.coaches || "—"}</Fixed></div>
            <div className="ds-field"><span>Time</span><Fixed icon={<Clock size={16} />}>{ampm(hhmm(visit.start))} - {ampm(hhmm(visit.end))}</Fixed></div>
          </div>
          {groups.map(({ key, rows, first, members }) => (
            <section key={key} className="lp-card lp-visit__card">
              <div className="lp-card__head">
                <div className="lp-card__title">
                  <h3>Lesson Plan{centreName ? ` — ${centreName}` : ""}</h3>
                  {rows.length > 0 && <span className="lp-count">{rows.length} {rows.length === 1 ? "Child" : "Children"}</span>}
                </div>
                {first.learningsFocus && <p className="lp-hint"><BookOpen size={15} color="#1d4ed8" />Learning focus: <b>{first.learningsFocus}</b></p>}
              </div>
              {members.map((s) => (
                <p key={s._id} className="lp-visit__coach">
                  <User size={15} /><b>{coachName(s)}</b>
                  <span>· {ampm(hhmm(s.startTime))} - {ampm(hhmm(s.endTime))} · {MEMBER_STATUS[s.status] || s.status || "Scheduled"}</span>
                </p>
              ))}
              {rows.length > 0 ? (
                <div className="lp-table-wrap">
                  <table className="lp-table lp-table--plan">
                    <PlanTableHead />
                    <tbody>
                      {rows.map((r, i) => {
                        const learning = rowLearning(r);
                        return (
                          <tr key={`${r.childName}-${i}`}>
                            <td>{i + 1}</td>
                            <td title={r.childName}><b>{r.childName}</b></td>
                            <td title={r.computer || undefined}><FixedValue value={r.computer} meta={fixedAppMeta} /></td>
                            {LEARNING_COLUMNS.map(({ key: column }) => (
                              <td key={column} title={learning[column] || undefined}>
                                <FixedLearning column={column} value={learning[column]} />
                                <PlanRefusalNote refusal={refusalOf(idOf(r.childId), column, learning[column])} />
                              </td>
                            ))}
                            <td title={r.note || undefined}>{r.note ? <span className="lp-input lp-input--fixed">{r.note}</span> : <span className="lp-fixed-empty">—</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="lp-visit__text">{first.description || "No child-wise plan was added for this visit."}</p>
              )}
              {(first.healthCheck || first.specialInstructions) && (
                <div className="lp-extras__panels lp-fixed-panels">
                  {first.healthCheck && (
                    <div>
                      <h3><span className="lp-extras__icon is-pink"><Heart size={16} fill="currentColor" /></span>Health Check</h3>
                      <p>{first.healthCheck}</p>
                    </div>
                  )}
                  {first.specialInstructions && (
                    <div>
                      <h3><span className="lp-extras__icon is-orange"><FileText size={16} /></span>Instructions</h3>
                      <p>{first.specialInstructions}</p>
                    </div>
                  )}
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

const TITLES = { create: "Create Schedule", view: "View Lesson Plan", edit: "Edit Lesson Plan" };
const NOTES = {
  create: "Prepared from updated Playground records and coach reports. Review and edit before creating the schedule.",
  view: "This is the lesson plan shared with the coaches for this schedule. Use Edit Schedule to change it.",
  edit: "Change the visit details or the plan, then Save Changes. Every coach on this schedule gets the updated plan.",
};

// A row of a saved plan. `base` remembers what was saved, so an unchanged row is saved back as it was.
const rowFromSaved = (r) => {
  const learning = learningFromSaved(r, STATIC_OPTIONS);
  return { computer: r.computer || "", note: r.note || "", learning, base: { oneOnOne: r.oneOnOne || "", category: r.category || "", learning } };
};

// Form state for an existing schedule (see AdminLessonPlanSchedule.jsx).
const formFromSchedule = (s) => ({
  date: s.date,
  balagruhaIds: [s.centreId],
  assignedTo: s.coaches.map((c) => c.id),
  startTime: hhmm(s.startTime),
  endTime: hhmm(s.endTime),
  healthCheck: s.lead.healthCheck || "",
  instructions: s.lead.specialInstructions || "",
  plan: Object.fromEntries(s.rows.map((r) => [r.id, rowFromSaved(r)])),
});

// mode "create" (default) makes new schedules. "view" and "edit" show one existing schedule, `existing`,
// on the same page: view is read-only, edit saves the changes back to that schedule's coaches.
export default function AdminLessonPlan({ balagruhas, users, centreOptions, coachOptions, onDone, mode = "create", existing = null, onOpen }) {
  const location = useLocation();
  const readOnly = mode === "view";
  const editing = mode === "edit";
  const [initial] = useState(() => {
    if (existing) return { form: formFromSchedule(existing), restored: false };
    const q = new URLSearchParams(location.search);
    const fresh = {
      date: q.get("date") || dateKey(new Date()),
      balagruhaIds: q.get("balagruha") ? [q.get("balagruha")] : [],
      assignedTo: q.get("coach") ? [q.get("coach")] : [],
      startTime: "10:00",
      endTime: "11:00",
      healthCheck: "",
      instructions: "",
      plan: {},
    };
    const draft = readDraft();
    // Restore a saved draft unless the admin came here for a different day.
    if (draft && (!q.get("date") || draft.date === q.get("date"))) return { form: { ...fresh, ...draft }, restored: true };
    return { form: fresh, restored: false };
  });
  const [form, setForm] = useState(initial.form);
  // Each child's records (children-progress) for the selected Balgruha, and published course content
  // (null until loaded; {} when it couldn't be).
  const [progress, setProgress] = useState({ centreId: "", ready: false, failed: false, byId: {}, maxScore: 30, columnNames: {} });
  const [catalog, setCatalog] = useState(null);
  const [generated, setGenerated] = useState(mode === "create");
  const [regenerating, setRegenerating] = useState(false);
  // Learning focus of the saved plan, to tell whether the edit changed it.
  const [startFocus] = useState(() => usedColumns(Object.values(initial.form.plan).map((row) => ({ learning: rowLearning(row) }))));
  // Attachments already on the schedule being edited; new files go in `files`.
  const [kept, setKept] = useState(() => existing?.lead.attachments || []);
  const [panels, setPanels] = useState(() => ({ health: Boolean(form.healthCheck), instructions: Boolean(form.instructions), attachments: kept.length > 0 }));
  // Files can't go into the localStorage draft, so they live outside the form.
  const [files, setFiles] = useState([]);
  const [previous, setPrevious] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  // View: what coaches recorded when a child refused a planned activity of this visit.
  const refusalOf = usePlanRefusals(readOnly ? existing?.centreId : "", readOnly ? existing?.date : "");

  useEffect(() => { if (initial.restored) showToast("Draft restored", "success"); }, [initial.restored]);

  const centreId = form.balagruhaIds[0] || "";
  const centre = balagruhas.find((b) => b._id === centreId);
  const userName = useMemo(() => Object.fromEntries(users.map((u) => [u._id || u.id, u.name])), [users]);
  const children = useMemo(() => {
    // An existing schedule lists the children in its saved plan, as they were planned. Only a schedule
    // without a plan (or moved to another Balgruha while editing) starts from the Balgruha's children.
    if (existing && centreId === existing.centreId && existing.rows.length) return existing.rows.map((r) => ({ _id: r.id, name: r.childName, childId: r.childId }));
    if (readOnly) return [];
    return centreId ? users.filter((u) => isStudent(u) && (u.balagruhaIds || []).some((id) => idOf(id) === centreId)) : [];
  }, [existing, readOnly, centreId, users]);
  const mentionPeople = useMemo(() => children.map((c) => ({ id: c._id || c.id, name: c.name })), [children]);
  const coachChoices = useMemo(() => {
    // Visits can be assigned to non-coach staff; keep the current assignees selectable.
    const extra = (existing?.coaches || []).filter((c) => !coachOptions.some((o) => o.id === c.id));
    return [...extra, ...coachOptions];
  }, [existing, coachOptions]);

  useEffect(() => {
    if (readOnly || !centreId) return undefined;
    let cancelled = false;
    setProgress({ centreId, ready: false, failed: false, byId: {}, maxScore: 30, columnNames: {} });
    loadProgress(centreId).then((loaded) => { if (!cancelled) setProgress(loaded); });
    return () => { cancelled = true; };
  }, [centreId, readOnly]);

  useEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => getLessonPlanCatalog())
      .then((res) => { if (!cancelled) setCatalog(res?.success !== false ? res?.data || {} : {}); })
      // Without it nothing is offered, and plans are proposed from the apps and tracker names only.
      .catch(() => { if (!cancelled) setCatalog({}); });
    return () => { cancelled = true; };
  }, [readOnly]);

  const options = useMemo(
    () => buildColumnOptions(catalog || {}, Object.values(progress.byId), progress.columnNames),
    [catalog, progress.byId, progress.columnNames]
  );
  // The apps, plus the Syllabus Tracker's Computer apps activities the children are graded in.
  const computerGroups = useMemo(
    () => [...COMPUTER_GROUPS, ...trackerComputerGroups(Object.values(progress.byId), progress.columnNames)],
    [progress.byId, progress.columnNames]
  );
  const progressReady = progress.ready && progress.centreId === centreId;
  const withRecords = children.filter((c) => {
    const p = progress.byId[c._id || c.id];
    return p && (Object.keys(p.syllabus || {}).length || p.gcompris);
  }).length;

  // Propose a plan for every child that doesn't have one yet, once their records and the course
  // content (which names them) are in; keeps edits when the list changes.
  useEffect(() => {
    if (readOnly || !children.length || !progressReady || !catalog) return;
    setForm((f) => {
      const missing = children.filter((c) => !f.plan[c._id || c.id]);
      if (!missing.length) return f;
      const plan = { ...f.plan };
      children.forEach((c, i) => {
        const id = c._id || c.id;
        if (!plan[id]) plan[id] = proposeRow(progress.byId[id], i, progress.maxScore, progress.columnNames, catalog);
      });
      return { ...f, plan };
    });
  }, [children, readOnly, progressReady, progress, catalog]);

  // A fresh proposal for every child from the latest records, replacing the table's changes.
  const regenerate = async () => {
    if (!centreId || !children.length) return;
    const confirmed = await confirmDialog({
      title: "Regenerate the plan?",
      html: "Every child's learning is replaced with a fresh proposal from the latest Playground records. Special instructions are kept.",
      confirmText: "Yes, regenerate",
      cancelText: "Keep my changes",
    });
    if (!confirmed) return;
    setRegenerating(true);
    const fresh = await loadProgress(centreId);
    setProgress(fresh);
    // Special instructions are the admin's own, so they stay.
    setForm((f) => ({
      ...f,
      plan: Object.fromEntries(children.map((c, i) => {
        const id = c._id || c.id;
        return [id, { ...proposeRow(fresh.byId[id], i, fresh.maxScore, fresh.columnNames, catalog || {}), note: f.plan[id]?.note || "" }];
      })),
    }));
    setGenerated(true);
    setRegenerating(false);
    showToast(fresh.failed ? "Records couldn't be loaded; the plan starts every child at the first scene" : "Plan regenerated", fresh.failed ? "error" : "success");
  };

  useEffect(() => {
    if (!centreId || !form.date) { setPrevious([]); return; }
    let cancelled = false;
    // The two latest visits before the date being planned (including ones still upcoming).
    const lastDay = addDays(fromKey(form.date), -1);
    getSchedules({ balagruhaIds: [centreId], startDate: dateKey(addDays(lastDay, -365)), endDate: dateKey(lastDay), status: [] })
      .then((result) => {
        if (cancelled) return;
        // One visit = one day at this Balgruha, with every coach who came that day.
        const byDay = {};
        flattenSchedules(result)
          .filter((s) => s.status !== "cancelled" && centreIdOf(s) === centreId)
          .forEach((s) => { (byDay[dateKey(scheduleDate(s))] ||= []).push(s); });
        const visits = Object.entries(byDay)
          .sort(([a], [b]) => (a < b ? 1 : -1))
          .slice(0, 2)
          .map(([key, list]) => {
            list.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
            const unique = (values) => [...new Set(values.filter(Boolean))];
            return {
              key,
              list,
              first: list[0],
              coaches: unique(list.map((s) => s.assignedToUser?.name || userName[coachIdOf(s)])).join(", "),
              start: list[0].startTime,
              end: list.reduce((latest, s) => (new Date(s.endTime) > new Date(latest) ? s.endTime : latest), list[0].endTime),
              focus: unique(list.flatMap((s) => [s.specialInstructions, s.learningsFocus])).join(", ") || list[0].title || "—",
            };
          });
        setPrevious(visits);
      })
      .catch(() => { if (!cancelled) setPrevious([]); });
    return () => { cancelled = true; };
  }, [centreId, form.date, userName]);

  const setRow = (id, patch) => setForm((f) => ({ ...f, plan: { ...f.plan, [id]: { ...f.plan[id], ...patch } } }));
  // `key` is "computer" or a learning column.
  const withCell = (row, key, value) => (key === "computer" ? { ...row, computer: value } : { ...row, learning: { ...emptyLearning(), ...rowLearning(row), [key]: value } });
  // A cell picked for one child is remembered in `manual`, so a later Select all / Clear all keeps it.
  const pickCell = (id, key, value) => setForm((f) => {
    const row = f.plan[id] || {};
    return { ...f, plan: { ...f.plan, [id]: { ...withCell(row, key, value), manual: { ...row.manual, [key]: true } } } };
  });

  // A dropdown's "Select all" / "Clear all": the same choice for every child in that column, except
  // other children's hand-picked cells. The child whose dropdown it was always gets it.
  const setForAll = (fromId, key, value, columnLabel) => {
    const keeps = (id, plan) => id !== fromId && Boolean(plan[id]?.manual?.[key]);
    setForm((f) => {
      const plan = { ...f.plan };
      children.forEach((c) => {
        const id = c._id || c.id;
        if (!keeps(id, f.plan)) plan[id] = withCell(plan[id] || {}, key, value);
      });
      return { ...f, plan };
    });
    const kept = children.filter((c) => keeps(c._id || c.id, form.plan)).length;
    const count = children.length - kept;
    const everyone = kept ? `${count} ${count === 1 ? "child" : "children"} (${kept} manual ${kept === 1 ? "pick" : "picks"} kept)` : `all ${count} ${count === 1 ? "child" : "children"}`;
    showToast(value ? `${value} set for ${everyone}` : `${columnLabel} cleared for ${everyone}`, "success");
  };

  // Rows of a saved plan carry the child's id when it is known (plans read from text may not have one).
  const planRows = () => children.map((c) => {
    const row = form.plan[c._id || c.id] || {};
    const { oneOnOne, category } = savedLearning(rowLearning(row), row.base);
    const childId = "childId" in c ? c.childId : c._id || c.id;
    return { ...(childId ? { childId } : {}), childName: c.name, computer: row.computer || COMPUTER_APPS[0].name, oneOnOne, category, note: row.note || "" };
  });

  const planText = () => children.map((c, i) => {
    const row = form.plan[c._id || c.id] || {};
    const { oneOnOne } = savedLearning(rowLearning(row), row.base);
    return `${i + 1}. ${c.name} — Computer: ${row.computer || "-"}; One-on-One: ${oneOnOne}${row.note ? `; Note: ${row.note}` : ""}`;
  }).join("\n");
  // Learning focus of the plan on screen, e.g. "Intermediate Scenes, Stories".
  const planFocus = () => usedColumns(children.map((c) => ({ learning: rowLearning(form.plan[c._id || c.id]) }))) || "To Be Decided";

  const validate = () => {
    if (!centreId) return "Select a Balgruha";
    if (!form.assignedTo.length) return "Select at least one coach";
    const s = new Date(`${form.date}T${form.startTime}:00`);
    const e = new Date(`${form.date}T${form.endTime}:00`);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return "Enter a valid date and time";
    if (s >= e) return "Start time must be before end time";
    return "";
  };

  // One schedule per coach: the API stores a single coach on each schedule.
  const create = async () => {
    const error = validate();
    if (error) { showToast(error, "error"); return false; }
    const startTime = new Date(`${form.date}T${form.startTime}:00`).toISOString();
    const endTime = new Date(`${form.date}T${form.endTime}:00`).toISOString();
    const schedule = {
      title: `${centre?.name || "Balgruha"} - Lesson Plan`,
      description: planText(),
      specialInstructions: form.instructions,
      healthCheck: form.healthCheck,
      learningsFocus: planFocus(),
      lessonPlan: {
        course: planFocus(),
        children: planRows(),
      },
      date: form.date,
      dateString: form.date,
      startTime,
      endTime,
    };
    setSaving(true);
    try {
      if (files.length) schedule.attachments = await uploadScheduleAttachments(files);
      for (const coachId of form.assignedTo) {
        const result = await createSchedule({ balagruhaIds: [centreId], assignedTo: [coachId], schedules: [schedule] });
        if (result && result.success === false) throw new Error(result.message === "Found overlapping schedules" ? `${userName[coachId] || "A coach"} already has a visit at this time` : result.message);
      }
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* storage unavailable */ }
      return true;
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Could not create the schedule", "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      showToast("Draft saved on this device", "success");
    } catch {
      showToast("Could not save the draft in this browser", "error");
    }
  };

  const createAndExit = async () => {
    if (await create()) { showToast("Schedule created", "success"); onDone(); }
  };

  // Sends the shared-page link in the Share visit format ("Sanstha : X", "Time : …", "ISF Faculty : …",
  // "Date : …", "Day : …", link); if the link can't be made, the plan goes out as text, as before.
  const share = async () => {
    // Asked for alongside create(), so the share sheet isn't held up by one more request.
    const keyRequest = validate() ? Promise.resolve("") : getScheduleShareKey(centreId, form.date).catch(() => "");
    if (!(await create())) return;
    const coaches = form.assignedTo.map((id) => userName[id]).filter(Boolean).join(", ");
    const key = await keyRequest;
    const visit = { date: form.date, startTime: `${form.date}T${form.startTime}:00`, endTime: `${form.date}T${form.endTime}:00` };
    const text = key
      ? visitLinkText({ visit, centreName: centre?.name, coachNames: coaches, url: visitShareUrl({ date: form.date, centreName: centre?.name, key }) })
      : `Lesson Plan — ${centre?.name}\n${longDate(form.date)}, ${ampm(form.startTime)} – ${ampm(form.endTime)}\nCoaches: ${coaches}\n\n${planText()}${form.instructions ? `\n\nGeneral instructions: ${form.instructions}` : ""}`;
    try {
      if (navigator.share) await navigator.share({ title: "Lesson Plan", text });
      else { await navigator.clipboard.writeText(text); showToast("Schedule created and copied to clipboard", "success"); }
    } catch { /* share sheet dismissed */ }
    onDone();
  };

  // Edit mode: saves the page back to this schedule. As in the old Edit Visit dialog, coaches who stay
  // keep their copy, added coaches get one and removed coaches' copies are deleted after a confirmation.
  const coachName = (id) => existing?.coaches.find((c) => c.id === id)?.name || userName[id] || "A coach";
  const saveChanges = async () => {
    const error = validate();
    if (error) { showToast(error, "error"); return; }
    const { lead, members } = existing;
    const hasPlan = children.length > 0;
    const focus = planFocus();
    const focusChanged = focus !== (startFocus || "To Be Decided");
    const fields = {
      title: centreId === existing.centreId && lead.title ? lead.title : `${centre?.name || "Balgruha"} - Lesson Plan`,
      description: hasPlan ? planText() : lead.description || "",
      specialInstructions: form.instructions,
      healthCheck: form.healthCheck,
      learningsFocus: hasPlan && (focusChanged || !lead.learningsFocus) ? focus : lead.learningsFocus || "",
      ...(hasPlan ? { lessonPlan: { course: focusChanged || !lead.lessonPlan?.course ? focus : lead.lessonPlan.course, children: planRows() } } : {}),
      date: form.date,
      dateString: form.date,
      startTime: new Date(`${form.date}T${form.startTime}:00`).toISOString(),
      endTime: new Date(`${form.date}T${form.endTime}:00`).toISOString(),
    };

    const pending = [...form.assignedTo];
    const keep = [];
    const spare = [];
    members.forEach((m) => {
      const i = pending.indexOf(coachIdOf(m));
      if (i >= 0) { keep.push([m, pending[i]]); pending.splice(i, 1); } else spare.push(m);
    });
    const reassign = Math.min(spare.length, pending.length);
    spare.slice(0, reassign).forEach((m) => keep.push([m, pending.shift()]));
    const removed = spare.slice(reassign);
    if (removed.length && !(await confirmDialog({
      title: "Remove coach from this schedule?",
      html: `<b>${escapeHtml(removed.map((m) => coachName(coachIdOf(m))).join(", "))}</b> will be removed from this schedule and their copy of it will be deleted.`,
      confirmText: "Yes, remove",
      cancelText: "Keep",
      danger: true,
    }))) return;

    setSaving(true);
    const settle = (promise) => promise.catch((e) => ({ success: false, message: e?.response?.data?.message || e?.message }));
    const check = (result, coachId) => {
      if (result && result.success === false) {
        throw new Error(/overlapping/i.test(result.message || "") ? `${coachName(coachId)} already has a visit at this time` : result.message || "Could not save the schedule");
      }
    };
    try {
      const uploaded = files.length ? await uploadScheduleAttachments(files) : [];
      const attachments = [...kept.map(({ _id, ...a }) => a), ...uploaded];
      for (const [member, coachId] of keep) check(await settle(updateSchedule({ ...fields, attachments, balagruhaId: centreId, assignedTo: coachId }, member._id)), coachId);
      for (const coachId of pending) {
        check(await settle(createSchedule({ balagruhaIds: [centreId], assignedTo: [coachId], schedules: [{ ...fields, ...(attachments.length ? { attachments } : {}) }] })), coachId);
      }
      for (const member of removed) check(await settle(deleteSchedule(member._id)), coachIdOf(member));
      showToast("Schedule updated", "success");
      onDone();
    } catch (err) {
      showToast(err?.message || "Could not save the schedule", "error");
    } finally {
      setSaving(false);
    }
  };

  // Edit mode: cancels this schedule for all its coaches (the old Edit Visit dialog's "Cancel Schedule").
  const cancelSchedule = async () => {
    const centreName = balagruhas.find((b) => b._id === existing.centreId)?.name || "Balgruha";
    const confirmed = await confirmDialog({
      title: "Cancel this schedule?",
      html: `<b>${escapeHtml(centreName)}</b><br/>${escapeHtml(longDate(existing.date))}, ${escapeHtml(ampm(hhmm(existing.startTime)))} – ${escapeHtml(ampm(hhmm(existing.endTime)))}<br/>Coach: ${escapeHtml(existing.coaches.map((c) => c.name).join(", "))}<br/><br/>The schedule will be cancelled for all its coaches.`,
      confirmText: "Yes, cancel schedule",
      cancelText: "Keep schedule",
      danger: true,
    });
    if (!confirmed) return;
    setSaving(true);
    try {
      for (const member of existing.members.filter((m) => m.status !== "cancelled")) {
        const result = await updateScheduleStatus(member._id, "cancelled");
        if (result && result.success === false) throw new Error(result.message);
      }
      resultDialog("success", "Schedule cancelled");
      onDone();
    } catch (err) {
      resultDialog("error", "Could not cancel the schedule", err?.response?.data?.message || err?.message || "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const addFiles = (list) => {
    const picked = Array.from(list || []);
    const tooBig = picked.filter((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig.length) showToast(`${tooBig.map((f) => f.name).join(", ")} is larger than 5 MB`, "error");
    setFiles((current) => [...current, ...picked.filter((f) => f.size <= MAX_ATTACHMENT_BYTES)]);
  };

  const coachChips = (selected) => selected.map((o) => (
    <span className="lp-chip" key={o.id}>
      {o.name}
      {!readOnly && <button type="button" aria-label={`Remove ${o.name}`} onClick={(e) => { e.stopPropagation(); setForm((f) => ({ ...f, assignedTo: f.assignedTo.filter((id) => id !== o.id) })); }}><X size={13} /></button>}
    </span>
  ));

  const coachNames = joinNames(form.assignedTo.map((id) => coachChoices.find((o) => o.id === id)?.name || userName[id]).filter(Boolean));

  return (
    <div className="lp-layout lp-layout--plan">
      <section className="lp-main">
        <header className="lp-title">
          <span className="ds-header__icon"><CalendarDays size={26} strokeWidth={1.8} /></span>
          <div>
            <h1>{TITLES[mode]}</h1>
            <p>Personalized lesson plan for each child</p>
          </div>
        </header>

        {existing?.slots.length > 1 && (
          <nav className="lp-slots" aria-label="Schedules at this Balgruha on this day">
            <span>{existing.slots.length} schedules at this Balgruha on this day:</span>
            {existing.slots.map((slot) => (
              <button key={slot.id} type="button" className={slot.current ? "is-current" : ""} aria-current={slot.current ? "true" : undefined} onClick={() => !slot.current && onOpen(slot.id)}>
                <Clock size={14} />{ampm(hhmm(slot.startTime))} – {ampm(hhmm(slot.endTime))}<small>{slot.coaches}</small>
              </button>
            ))}
          </nav>
        )}

        <div className="lp-fields">
          <div className="ds-field">
            <span>Date {!readOnly && <em>*</em>}</span>
            {readOnly ? <Fixed icon={<CalendarDays size={16} />}>{longDate(form.date)}</Fixed> : (
              <NativePickerField type="date" chevron icon={<CalendarDays size={16} />} value={form.date} display={longDate(form.date)} onChange={(date) => setForm({ ...form, date })} className="lp-control" />
            )}
          </div>
          <div className="ds-field">
            <span>Balgruha {!readOnly && <em>*</em>}</span>
            {readOnly ? <Fixed icon={<House size={16} fill="currentColor" />}>{centre?.name || "—"}</Fixed> : (
              <CheckPicker className="lp-control" icon={<House size={16} fill="currentColor" />} options={centreOptions} value={form.balagruhaIds} onChange={(balagruhaIds) => setForm({ ...form, balagruhaIds })} placeholder="Select Balgruha" searchPlaceholder="Search Balgruha" />
            )}
          </div>
          <div className="ds-field">
            <span>Coaches {!readOnly && <em>*</em>}</span>
            {readOnly ? <Fixed icon={<Users size={17} fill="currentColor" />}>{coachChips(existing.coaches)}</Fixed> : (
              <CheckPicker className="lp-control" icon={<Users size={17} fill="currentColor" />} options={coachChoices} value={form.assignedTo} multiple renderValue={coachChips} onChange={(assignedTo) => setForm({ ...form, assignedTo })} placeholder="Select Coaches" searchPlaceholder="Search Coach" />
            )}
          </div>
          <div className="ds-field">
            <span>Start Time {!readOnly && <em>*</em>}</span>
            {readOnly ? <Fixed icon={<Clock size={16} />}>{ampm(form.startTime)}</Fixed> : (
              <NativePickerField type="time" chevron icon={<Clock size={16} />} value={form.startTime} display={ampm(form.startTime)} onChange={(startTime) => setForm({ ...form, startTime })} className="lp-control" />
            )}
          </div>
          <div className="ds-field">
            <span>End Time {!readOnly && <em>*</em>}</span>
            {readOnly ? <Fixed icon={<Clock size={16} />}>{ampm(form.endTime)}</Fixed> : (
              <NativePickerField type="time" chevron icon={<Clock size={16} />} value={form.endTime} display={ampm(form.endTime)} onChange={(endTime) => setForm({ ...form, endTime })} className="lp-control" />
            )}
          </div>
          {!readOnly && <p className="lp-fields__hint">Select date and Balgruha to generate the plan automatically.</p>}
        </div>

        <section className="lp-card">
          <div className="lp-card__head">
            <div className="lp-card__title">
              <h2>{mode === "create" ? "Proposed Lesson Plan" : "Lesson Plan"}{centre ? ` — ${centre.name}` : ""}</h2>
              {centre && <span className="lp-count">{children.length} {children.length === 1 ? "Child" : "Children"}</span>}
            </div>
            {readOnly ? (
              <p className="lp-hint">Status <span className={`ds-status ds-status--${existing.status}`}>{STATUS_LABEL[existing.status] || existing.status}</span></p>
            ) : (
              <div className="lp-card__actions">
                {generated && <span className="lp-generated"><Sparkles size={16} />Generated by PG</span>}
                <button type="button" className="lp-regenerate" disabled={!centreId || !children.length || regenerating || saving} onClick={regenerate}>
                  <RefreshCw size={16} className={regenerating ? "is-spinning" : ""} />{regenerating ? "Regenerating…" : "Regenerate Plan"}
                </button>
              </div>
            )}
          </div>
          <p className="lp-note">
            <Info size={18} fill="#2563eb" color="#fff" />
            <span>{NOTES[mode]}</span>
            {!readOnly && centreId && children.length > 0 && (
              <small>
                {!progressReady ? "Loading records…" : progress.failed ? "Records couldn't be loaded" : `${withRecords} of ${children.length} ${children.length === 1 ? "child has" : "children have"} records`}
              </small>
            )}
          </p>

          <div className="lp-table-wrap">
            <table className="lp-table lp-table--plan">
              <PlanTableHead />
              <tbody>
                {children.map((c, i) => {
                  const id = c._id || c.id;
                  const row = form.plan[id] || {};
                  const learning = rowLearning(row);
                  return (
                    <tr key={id}>
                      <td>{i + 1}</td>
                      <td><b>{c.name}</b></td>
                      {readOnly ? (
                        <>
                          <td><FixedValue value={row.computer} meta={fixedAppMeta} /></td>
                          {LEARNING_COLUMNS.map(({ key }) => (
                            <td key={key}>
                              <FixedLearning column={key} value={learning[key]} />
                              <PlanRefusalNote refusal={refusalOf(c.childId, key, learning[key])} />
                            </td>
                          ))}
                          <td>{row.note ? <span className="lp-input lp-input--fixed">{row.note}</span> : <span className="lp-fixed-empty">—</span>}</td>
                        </>
                      ) : (
                        <>
                          <td>
                            <LessonPlanPicker
                              value={row.computer || COMPUTER_APPS[0].name}
                              groups={computerGroups}
                              allowEmpty={false}
                              icon={appMeta}
                              label={`Computer Apps for ${c.name}`}
                              onChange={(computer) => pickCell(id, "computer", computer)}
                              onSelectAll={(computer) => setForAll(id, "computer", computer, "Computer Apps")}
                            />
                          </td>
                          {LEARNING_COLUMNS.map(({ key, label }) => (
                            <td key={key}>
                              <LessonPlanPicker
                                value={learning[key] || ""}
                                groups={options[key]}
                                icon={COLUMN_ICON_OF[key]}
                                label={`${label} for ${c.name}`}
                                onChange={(value) => pickCell(id, key, value)}
                                onSelectAll={(value) => setForAll(id, key, value, label)}
                              />
                            </td>
                          ))}
                          <td><input className="lp-input" value={row.note || ""} placeholder="Add instruction" aria-label={`Special instructions for ${c.name}`} onChange={(e) => setRow(id, { note: e.target.value })} /></td>
                        </>
                      )}
                    </tr>
                  );
                })}
                {!children.length && (
                  <tr>
                    <td colSpan={4 + LEARNING_COLUMNS.length} className={`lp-empty${readOnly && existing.lead.description ? " lp-empty--text" : ""}`}>
                      {readOnly
                        ? existing.lead.description || "No child-wise plan was added for this schedule."
                        : centreId ? "No children are assigned to this Balgruha yet." : "Select a Balgruha to load its children."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {readOnly ? (
            (form.healthCheck || form.instructions || kept.length > 0) && (
              <div className="lp-extras__panels lp-fixed-panels">
                {form.healthCheck && (
                  <div>
                    <h3><span className="lp-extras__icon is-pink"><Heart size={16} fill="currentColor" /></span>Health Check</h3>
                    <p>{form.healthCheck}</p>
                  </div>
                )}
                {form.instructions && (
                  <div>
                    <h3><span className="lp-extras__icon is-orange"><FileText size={16} /></span>General Instructions</h3>
                    <p>{form.instructions}</p>
                  </div>
                )}
                {kept.length > 0 && (
                  <div>
                    <h3><span className="lp-extras__icon"><Paperclip size={16} /></span>Attachments</h3>
                    <ul>
                      {kept.map((a) => (
                        <li key={a._id || a.s3Key}>
                          <button type="button" className="lp-file-link" onClick={() => openScheduleAttachment(existing.lead._id, a)}><Paperclip size={13} />{a.filename || "Attachment"}</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )
          ) : (
          <>
          <div className="lp-extras">
            <button type="button" className={panels.health ? "is-open" : ""} onClick={() => setPanels((p) => ({ ...p, health: !p.health }))}><span className="lp-extras__icon is-pink"><Heart size={17} fill="currentColor" /></span><b className="is-pink"><Plus size={15} />Health Check</b> (Optional)</button>
            <button type="button" className={panels.instructions ? "is-open" : ""} onClick={() => setPanels((p) => ({ ...p, instructions: !p.instructions }))}><span className="lp-extras__icon is-orange"><FileText size={17} /></span><b><Plus size={15} />General Instructions</b></button>
            <button type="button" className={panels.attachments ? "is-open" : ""} onClick={() => setPanels((p) => ({ ...p, attachments: !p.attachments }))}><span className="lp-extras__icon"><Paperclip size={17} /></span><b><Plus size={15} />Attachments</b></button>
          </div>
          {(panels.health || panels.instructions || panels.attachments) && (
            <div className="lp-extras__panels">
              {panels.health && (
                <div className="ds-field">
                  <span>Health Check</span>
                  <MentionTextarea value={form.healthCheck} onChange={(healthCheck) => setForm((f) => ({ ...f, healthCheck }))} people={mentionPeople} placeholder="e.g. @Mayur has a cough, check temperature" allOption />
                </div>
              )}
              {panels.instructions && <label className="ds-field"><span>General Instructions</span><textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="Add general instructions for this visit" /></label>}
              {panels.attachments && (
                <div className="ds-field">
                  <span>Attachments</span>
                  <label className="lp-file"><Paperclip size={15} />Choose files<input type="file" multiple accept={ATTACHMENT_ACCEPT} onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} /></label>
                  {kept.map((a) => (
                    <span key={a._id || a.s3Key} className="lp-chip">
                      <button type="button" className="lp-file-link" onClick={() => openScheduleAttachment(existing.lead._id, a)}>{a.filename || "Attachment"}</button>
                      <button type="button" aria-label={`Remove ${a.filename || "attachment"}`} onClick={() => setKept((list) => list.filter((x) => x !== a))}><X size={13} /></button>
                    </span>
                  ))}
                  {files.map((file, i) => (
                    <span key={`${file.name}-${i}`} className="lp-chip">
                      {file.name} <small>({fileSize(file.size)})</small>
                      <button type="button" aria-label={`Remove ${file.name}`} onClick={() => setFiles((list) => list.filter((_, j) => j !== i))}><X size={13} /></button>
                    </span>
                  ))}
                  <small>PDF, images, Word, Excel, PowerPoint, TXT or CSV, up to 5 MB each. Coaches see these with the schedule. Files aren't kept in drafts.</small>
                </div>
              )}
            </div>
          )}
          </>
          )}
        </section>

        <section className="lp-previous">
          <h3><Clock size={18} />Previous Two Visits <small>(For Reference)</small></h3>
          <table className="lp-table lp-table--compact">
            <thead><tr><th>Date</th><th>Coaches</th><th>Time</th><th>Learning &amp; Focus</th><th>Action</th></tr></thead>
            <tbody>
              {previous.map((visit) => (
                <tr key={visit.key}>
                  <td>{shortDate(scheduleDate(visit.first))}</td>
                  <td>{visit.coaches || "—"}</td>
                  <td>{ampm(hhmm(visit.start))} - {ampm(hhmm(visit.end))}</td>
                  <td>{visit.focus}</td>
                  <td><button type="button" className="lp-view" onClick={() => setViewing(visit)}><Eye size={15} />View</button></td>
                </tr>
              ))}
              {!previous.length && <tr><td colSpan={5} className="lp-empty">{centreId ? "No earlier visits for this Balgruha." : "Select a Balgruha to see its last two visits."}</td></tr>}
            </tbody>
          </table>
        </section>

        <footer className="lp-footer">
          <button type="button" className="lp-back" onClick={onDone}><ArrowLeft size={20} />Back to List</button>
          <div>
            {mode === "create" && (
              <>
                <button type="button" className="lp-btn lp-btn--outline" disabled={saving} onClick={saveDraft}><Save size={19} />Save as Draft</button>
                <button type="button" className="lp-btn lp-btn--orange" disabled={saving} onClick={createAndExit}><CalendarDays size={19} />{saving ? "Creating…" : "Create Schedule"}</button>
                <button type="button" className="lp-btn lp-btn--outline" disabled={saving} onClick={share}><Share2 size={19} />Share Schedule</button>
                {coachNames && <p className="lp-footer__note">Create Schedule sends the reviewed plan to {coachNames}.</p>}
              </>
            )}
            {readOnly && <button type="button" className="lp-btn lp-btn--orange" onClick={() => onOpen(existing.id, "edit")}><Pencil size={19} />Edit Schedule</button>}
            {editing && (
              <>
                {existing.status !== "cancelled" && <button type="button" className="lp-btn lp-btn--danger" disabled={saving} onClick={cancelSchedule}><Trash2 size={19} />Cancel Schedule</button>}
                <button type="button" className="lp-btn lp-btn--orange" disabled={saving} onClick={saveChanges}><Save size={19} />{saving ? "Saving…" : "Save Changes"}</button>
              </>
            )}
          </div>
        </footer>
      </section>

      {viewing && (
        <PreviousVisitDialog
          visit={viewing}
          centreName={centre?.name}
          coachName={(s) => s.assignedToUser?.name || userName[coachIdOf(s)] || "Coach"}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
